import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckSquare, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DataValidation = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    validationNotes: initialData.validationNotes || '',
    autoValidation: initialData.autoValidation || true
  });

  const [validationResults, setValidationResults] = useState({
    dataCompleteness: { status: 'pending', issues: [] },
    calculationAccuracy: { status: 'pending', issues: [] },
    dataConsistency: { status: 'pending', issues: [] },
    sourceVerification: { status: 'pending', issues: [] },
    unitConsistency: { status: 'pending', issues: [] }
  });

  const validationCategories = useMemo(() => [
    {
      key: 'dataCompleteness',
      title: t('workflow.draftReport.dataValidation.validationCategories.dataCompleteness.title'),
      description: t('workflow.draftReport.dataValidation.validationCategories.dataCompleteness.description'),
      icon: CheckSquare
    },
    {
      key: 'calculationAccuracy',
      title: t('workflow.draftReport.dataValidation.validationCategories.calculationAccuracy.title'),
      description: t('workflow.draftReport.dataValidation.validationCategories.calculationAccuracy.description'),
      icon: CheckSquare
    },
    {
      key: 'dataConsistency',
      title: t('workflow.draftReport.dataValidation.validationCategories.dataConsistency.title'),
      description: t('workflow.draftReport.dataValidation.validationCategories.dataConsistency.description'),
      icon: CheckSquare
    },
    {
      key: 'sourceVerification',
      title: t('workflow.draftReport.dataValidation.validationCategories.sourceVerification.title'),
      description: t('workflow.draftReport.dataValidation.validationCategories.sourceVerification.description'),
      icon: CheckSquare
    },
    {
      key: 'unitConsistency',
      title: t('workflow.draftReport.dataValidation.validationCategories.unitConsistency.title'),
      description: t('workflow.draftReport.dataValidation.validationCategories.unitConsistency.description'),
      icon: CheckSquare
    }
  ], [t]);

  const runValidation = useCallback(() => {
    // Simulate validation process
    const results = {};
    
    validationCategories.forEach(category => {
      const randomStatus = Math.random() > 0.3 ? 'passed' : 'failed';
      const issues = randomStatus === 'failed' ? [
        t('workflow.draftReport.dataValidation.sampleIssues.missingData'),
        t('workflow.draftReport.dataValidation.sampleIssues.inconsistentFormatting'),
        t('workflow.draftReport.dataValidation.sampleIssues.calculationError')
      ].slice(0, Math.floor(Math.random() * 3) + 1) : [];
      
      results[category.key] = {
        status: randomStatus,
        issues: issues
      };
    });

    setValidationResults(results);
  }, [validationCategories, t]);

  useEffect(() => {
    if (formData.autoValidation) {
      runValidation();
    }
  }, [formData.autoValidation, runValidation]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'passed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const allValidationsPassed = Object.values(validationResults).every(result => result.status === 'passed');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center mb-6">
          <CheckSquare className="w-8 h-8 text-blue-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{t('workflow.draftReport.dataValidation.title')}</h2>
            <p className="text-gray-600">{t('workflow.draftReport.dataValidation.description')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Auto Validation Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-800">{t('workflow.draftReport.dataValidation.automaticValidation')}</h3>
              <p className="text-sm text-gray-600">{t('workflow.draftReport.dataValidation.automaticValidationDescription')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.autoValidation}
                onChange={(e) => handleInputChange('autoValidation', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Manual Validation Button */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={runValidation}
              className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-200"
            >
              {t('workflow.draftReport.dataValidation.runDataValidation')}
            </button>
          </div>

          {/* Validation Results */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">{t('workflow.draftReport.dataValidation.validationResults')}</h3>
            
            {validationCategories.map(category => {
              const result = validationResults[category.key];
              const IconComponent = category.icon;
              
              return (
                <div
                  key={category.key}
                  className={`p-4 border rounded-lg ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <IconComponent className="w-5 h-5 mr-3 mt-0.5" />
                      <div>
                        <h4 className="font-medium">{category.title}</h4>
                        <p className="text-sm opacity-80">{category.description}</p>
                        
                        {result.issues.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium mb-1">{t('workflow.draftReport.dataValidation.issuesFound')}</p>
                            <ul className="text-sm space-y-1">
                              {result.issues.map((issue, index) => (
                                <li key={index} className="flex items-start">
                                  <span className="text-red-500 mr-2">•</span>
                                  {issue}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0">
                      {getStatusIcon(result.status)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall Status */}
          <div className={`p-4 rounded-lg border ${
            allValidationsPassed 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <div className="flex items-center">
              {allValidationsPassed ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : (
                <AlertTriangle className="w-5 h-5 mr-2" />
              )}
              <span className="font-medium">
                {allValidationsPassed 
                  ? t('workflow.draftReport.dataValidation.allValidationsPassed')
                  : t('workflow.draftReport.dataValidation.someIssuesDetected')
                }
              </span>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('workflow.draftReport.dataValidation.additionalNotes')}
            </label>
            <textarea
              value={formData.validationNotes}
              onChange={(e) => handleInputChange('validationNotes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('workflow.draftReport.dataValidation.additionalNotesPlaceholder')}
            />
          </div>


        </form>
      </div>
    </div>
  );
};

export default DataValidation;
